/**
 * SortableMenuItem - Sortable menu item component with drag-and-drop
 */
import { GripVertical, Edit, Trash2, Eye, EyeOff, ExternalLink, Trophy, Calendar, Save, X } from "lucide-react";
import { Button } from "../../ui/button";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const SortableMenuItem = ({ 
  item, 
  editingId, 
  formData, 
  setFormData, 
  saving, 
  handleUpdate, 
  handleDelete, 
  handleToggleVisibility, 
  startEdit, 
  cancelEdit 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  // Check if this is a protected item (can't be deleted)
  const isProtected = item.href === "/guide" || item.is_protected;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border ${item.is_visible !== false ? 'border-white/10 bg-zinc-900/50' : 'border-zinc-800 bg-zinc-900/20'} ${item.item_type === 'event' ? 'border-l-2 border-l-yellow-500' : ''} ${isDragging ? 'shadow-lg shadow-orange-500/20' : ''}`}
    >
      {editingId === item.id ? (
        /* Edit Mode */
        <form onSubmit={handleUpdate} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Display Name</label>
              <input 
                type="text" 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                required 
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Link</label>
              <input 
                type="text" 
                value={formData.href} 
                onChange={(e) => setFormData({ ...formData, href: e.target.value })} 
                required 
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
              />
            </div>
          </div>
          
          {/* Display Mode for Events with Logo */}
          {formData.item_type === "event" && formData.event_logo_url && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30">
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Display Mode in Menu</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="edit_display_mode" 
                    value="name" 
                    checked={formData.display_mode === "name"}
                    onChange={() => setFormData({ ...formData, display_mode: "name" })}
                    className="text-orange-500"
                  />
                  <span className="text-sm text-white">Show Name</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="edit_display_mode" 
                    value="logo" 
                    checked={formData.display_mode === "logo"}
                    onChange={() => setFormData({ ...formData, display_mode: "logo" })}
                    className="text-orange-500"
                  />
                  <span className="text-sm text-white">Show Logo</span>
                  <img src={formData.event_logo_url} alt="" className="h-4 w-auto object-contain" />
                </label>
              </div>
            </div>
          )}

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.is_external} 
                onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })} 
              />
              External Link
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.is_visible !== false} 
                onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })} 
              />
              Visible
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={saving} className="bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1 h-auto rounded-none">
              <Save className="w-3 h-3 mr-1" />
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button type="button" onClick={cancelEdit} className="bg-zinc-700 text-white hover:bg-zinc-600 text-xs px-3 py-1 h-auto rounded-none">
              <X className="w-3 h-3 mr-1" />Cancel
            </Button>
          </div>
        </form>
      ) : (
        /* View Mode */
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 text-zinc-600 hover:text-orange-500 transition-colors touch-none"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4" />
            </button>
            
            {/* Event Logo (if display mode is logo) */}
            {item.item_type === "event" && item.display_mode === "logo" && item.event_logo_url && (
              <img src={item.event_logo_url} alt="" className="w-8 h-8 object-contain" />
            )}
            
            <div>
              <div className="flex items-center gap-2">
                <span className={`font-bold ${item.is_visible !== false ? 'text-white' : 'text-zinc-500'}`}>
                  {item.name}
                </span>
                {item.item_type === "event" && (
                  <>
                    <Trophy className="w-3 h-3 text-yellow-500" />
                    {item.display_mode === "logo" && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-500 uppercase">Logo</span>
                    )}
                  </>
                )}
                {item.is_external && (
                  <ExternalLink className="w-3 h-3 text-zinc-500" />
                )}
                {item.is_visible === false && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 uppercase">Hidden</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-zinc-500">{item.href}</code>
                {item.item_type === "event" && item.event_dates && (
                  <span className="text-[10px] text-zinc-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {item.event_dates}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => handleToggleVisibility(item)} 
              className={`p-2 ${item.is_visible !== false ? 'text-green-500 hover:text-green-400' : 'text-zinc-600 hover:text-zinc-400'}`}
              title={item.is_visible !== false ? "Hide from menu" : "Show in menu"}
            >
              {item.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button 
              onClick={() => startEdit(item)} 
              className="p-2 text-zinc-500 hover:text-white"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
            {isProtected ? (
              <span 
                className="p-2 text-zinc-700 cursor-not-allowed"
                title="This item cannot be deleted"
              >
                <Trash2 className="w-4 h-4" />
              </span>
            ) : (
              <button 
                onClick={() => handleDelete(item.id)} 
                className="p-2 text-zinc-500 hover:text-red-500"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SortableMenuItem;
